import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DEFAULT_PAGE_SIZE, PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import { DATE_DISPLAY_FORMAT, toApiDate } from "../../utils/dateUtils";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import { ExpandLess, ExpandMore, Close, ArrowForward } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import auditLogService from "../../services/auditLogService";
import courseService from "../../services/courseService";
import { resolveUserDisplayName } from "../../utils/userDisplay";
import {
  buildReferenceMap,
  collectReferenceIds,
  unwrapListPayload,
} from "../../utils/auditReferenceResolver";
import {
  getAuditActionLabel,
  getAuditEntityLabel,
  getAuditSummary as buildAuditSummary,
} from "../../utils/auditLabels";
import {
  getAuditDetailDiffs,
  formatAuditFieldValue,
  describeAuditChanges,
} from "../../utils/auditDiff";
import { parseUserAgent } from "../../utils/parseUserAgent";

const renderAuditDetailContent = (log, formatValue) => {
  const diffs = getAuditDetailDiffs(log.oldValue, log.newValue, log.action, log.entityType);

  if (log.action === "update") {
    if (diffs.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No comparable changes found in this log. If you changed timetable status, try toggling it again — new activity logs will show Status and Live timetable clearly.
        </Typography>
      );
    }

    return (
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "background.paper",
        }}
      >
        {diffs.map((diff, index) => {
          if (diff.field === "_summary" || diff.field === "_relatedUpdates" || diff.isSummary) {
            return (
              <Box
                key={diff.field}
                sx={{
                  px: 2,
                  py: 1.5,
                  borderTop: index > 0 ? "1px solid" : "none",
                  borderColor: "divider",
                  bgcolor: "grey.50",
                }}
              >
                <Typography variant="body2">{formatValue(diff.newFieldValue, diff.field)}</Typography>
              </Box>
            );
          }

          return (
            <Box
              key={diff.field}
              sx={{
                display: "flex",
                alignItems: { xs: "flex-start", sm: "center" },
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 0.5, sm: 2 },
                px: 2,
                py: 1.5,
                borderTop: index > 0 ? "1px solid" : "none",
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" sx={{ minWidth: { sm: 180 }, fontWeight: 600, color: "text.primary" }}>
                {diff.fieldLabel}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="body2" color="text.secondary">
                  {formatValue(diff.oldFieldValue, diff.field)}
                </Typography>
                <ArrowForward sx={{ fontSize: 16, color: "warning.main" }} />
                <Typography variant="body2" fontWeight={600} color="warning.dark">
                  {formatValue(diff.newFieldValue, diff.field)}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  }

  if (diffs.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No additional details were recorded.
      </Typography>
    );
  }

  const accentColor = log.action === "create" ? "success" : "error";

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: `${accentColor}.light`,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: `${accentColor}.50`,
      }}
    >
      <Table size="small">
        <TableBody>
          {diffs.map((diff) => (
            <TableRow key={diff.field} sx={{ "&:last-child td": { borderBottom: 0 } }}>
              <TableCell
                sx={{
                  width: "38%",
                  fontWeight: 600,
                  color: "text.secondary",
                  borderColor: `${accentColor}.light`,
                  verticalAlign: "top",
                }}
              >
                {diff.fieldLabel}
              </TableCell>
              <TableCell sx={{ borderColor: `${accentColor}.light`, color: "text.primary", fontWeight: 500 }}>
                {formatValue(diff.displayValue ?? diff.newFieldValue ?? diff.oldFieldValue, diff.field)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

const ACTION_OPTIONS = ["create", "read", "read_list", "update", "delete"];

const getActionDescription = getAuditActionLabel;
const getEntityDisplayName = getAuditEntityLabel;

const getActionChipColor = (action) => {
  if (action === "delete") return "error";
  if (action === "update") return "warning";
  if (action === "create") return "success";
  return "primary";
};

const getActionIcon = (action) => {
  const icons = {
    create: "➕",
    read: "👁️",
    read_list: "📋",
    update: "✏️",
    delete: "🗑️"
  };
  return icons[action] || "📄";
};

const formatAuditDate = (formatterArg) => {
  const rawValue =
    formatterArg && typeof formatterArg === "object" && "value" in formatterArg
      ? formatterArg.value
      : formatterArg;

  if (!rawValue) return "—";

  const parsed = new Date(rawValue);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleString();
};

const AuditLogs = () => {
  const [user] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  });
  const userRole = (user?.role?.slug || user?.role || "").toLowerCase();
  const isSuperAdmin = userRole === "superadmin";

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    totalItems: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [referenceMaps, setReferenceMaps] = useState({
    courses: {},
    classes: {},
    departments: {},
    teachers: {},
  });

  const formatValue = useCallback(
    (value, field) => formatAuditFieldValue(value, field, { references: referenceMaps }),
    [referenceMaps],
  );

  const getAuditSummary = useCallback(
    (log) => log?.summary || buildAuditSummary(log, referenceMaps),
    [referenceMaps],
  );

  useEffect(() => {
    let cancelled = false;

    const loadReferenceMaps = async () => {
      try {
        const [coursesRes, classesRes] = await Promise.all([
          courseService.getCourseNames().catch(() => null),
          courseService.getClassNames().catch(() => null),
        ]);

        if (cancelled) return;

        setReferenceMaps((prev) => ({
          ...prev,
          courses: {
            ...prev.courses,
            ...buildReferenceMap(unwrapListPayload(coursesRes)),
          },
          classes: {
            ...prev.classes,
            ...buildReferenceMap(unwrapListPayload(classesRes), {
              nameKeys: ["name", "className", "title"],
            }),
          },
        }));
      } catch {
        // Non-fatal — IDs will be shown as fallback
      }
    };

    loadReferenceMaps();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!rows.length) return;

    const missingCourseIds = [...collectReferenceIds(rows).courses].filter(
      (id) => !referenceMaps.courses[id],
    );

    if (!missingCourseIds.length) return;

    let cancelled = false;

    Promise.all(
      missingCourseIds.slice(0, 10).map(async (id) => {
        try {
          const response = await courseService.getCourseDetailsById(id);
          const course = response?.data?.data || response?.data;
          const name = course?.name || course?.courseName;
          return name ? { id, name } : null;
        } catch {
          return null;
        }
      }),
    ).then((results) => {
      if (cancelled) return;
      const courseUpdates = results.filter(Boolean);
      if (!courseUpdates.length) return;

      setReferenceMaps((prev) => ({
        ...prev,
        courses: {
          ...prev.courses,
          ...Object.fromEntries(courseUpdates.map(({ id, name }) => [String(id), name])),
        },
      }));
    });

    return () => {
      cancelled = true;
    };
  }, [rows, referenceMaps.courses]);

  useEffect(() => {
    if (!selectedLog) return;

    const diffs = getAuditDetailDiffs(
      selectedLog.oldValue,
      selectedLog.newValue,
      selectedLog.action,
      selectedLog.entityType,
    );

    const courseIds = diffs
      .filter((diff) => diff.field === "courseId")
      .map((diff) => diff.displayValue ?? diff.newFieldValue ?? diff.oldFieldValue)
      .filter((id) => id != null && !referenceMaps.courses[String(id)]);

    if (!courseIds.length) return;

    let cancelled = false;

    Promise.all(
      [...new Set(courseIds.map(String))].map(async (id) => {
        try {
          const response = await courseService.getCourseDetailsById(id);
          const course = response?.data?.data || response?.data;
          const name = course?.name || course?.courseName;
          return name ? { id, name } : null;
        } catch {
          return null;
        }
      }),
    ).then((results) => {
      if (cancelled) return;
      const courseUpdates = results.filter(Boolean);
      if (!courseUpdates.length) return;

      setReferenceMaps((prev) => ({
        ...prev,
        courses: {
          ...prev.courses,
          ...Object.fromEntries(courseUpdates.map(({ id, name }) => [String(id), name])),
        },
      }));
    });

    return () => {
      cancelled = true;
    };
  }, [selectedLog, referenceMaps.courses]);

  const [filters, setFilters] = useState({
    entityType: "",
    entityId: "",
    action: "",
    changedBy: "",
    fromDate: "",
    toDate: "",
    piiRevealOnly: "", // "" = all, "true" = only audited PII-reveal events
  });

  const fetchLogs = useCallback(
    async (page = 1, limit = pagination.limit, filterOverride = null) => {
      setLoading(true);
      setError("");
      try {
        const activeFilters = filterOverride ?? filters;
        const params = { page, limit, ...activeFilters };
        if (params.entityId) {
          params.entityId = String(params.entityId).trim().replace(/^id:\s*/i, "");
        }
        Object.keys(params).forEach((key) => {
          if (params[key] === "" || params[key] == null) delete params[key];
        });

        const response = await auditLogService.getAuditLogs(params);
        const payload = response?.data?.data || {};
        const items = payload.items || [];
        const mappedRows = items.map((item) => ({
            ...item,
            id: item.id,
            changedByName:
              item.changedByDisplayName ||
              resolveUserDisplayName(item.changedByUser, { fallback: "System" }),
            summary: item.summary || buildAuditSummary(item, referenceMaps),
          }));
        mappedRows.sort((a, b) => new Date(b.changedAt || 0) - new Date(a.changedAt || 0));
        setRows(mappedRows);
        setPagination(payload.pagination || { page, limit, totalItems: 0, totalPages: 0 });
      } catch (err) {
        setError(err?.response?.data?.message || "Failed to load audit logs.");
      } finally {
        setLoading(false);
      }
    },
    [filters, pagination.limit],
  );

  useEffect(() => {
    setRows((currentRows) =>
      currentRows.length
        ? currentRows.map((row) => ({
            ...row,
            summary: buildAuditSummary(row, referenceMaps),
          }))
        : currentRows,
    );
  }, [referenceMaps]);

  useEffect(() => {
    fetchLogs(1, pagination.limit);
  }, [fetchLogs, pagination.limit]);

  const columns = useMemo(
    () => [
      { field: "id", headerName: "Log ID", width: 90 },
      { field: "entityId", headerName: "Entity ID", width: 100 },
      {
        field: "summary",
        headerName: "What Happened",
        width: 250,
        renderCell: (params) => {
          const summary = getAuditSummary(params.row);
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" fontWeight={500}>
                {summary}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "entityType",
        headerName: "Entity Type",
        width: 130,
        renderCell: (params) => getEntityDisplayName(params.value),
      },
      {
        field: "action",
        headerName: "Action",
        width: 120,
        renderCell: (params) => (
          <Chip
            size="small"
            label={getActionDescription(params.value)}
            color={getActionChipColor(params.value)}
            sx={{ cursor: 'pointer' }}
          />
        ),
      },
      { field: "changedByName", headerName: "Updated By", width: 180 },
      {
        field: "changedAt",
        headerName: "Updated At",
        width: 170,
        valueFormatter: formatAuditDate,
      },
      {
        field: "changesPreview",
        headerName: "Changes",
        width: 200,
        renderCell: (params) => {
          const { oldValue, newValue, action } = params.row || {};
          
          if (action === "create") {
            return (
              <Typography variant="caption" color="success.main" sx={{ fontWeight: 500 }}>
                New {getEntityDisplayName(params.row?.entityType)} created
              </Typography>
            );
          }
          
          if (action === "delete") {
            return (
              <Typography variant="caption" color="error.main" sx={{ fontWeight: 500 }}>
                {getEntityDisplayName(params.row?.entityType)} deleted
              </Typography>
            );
          }
          
          if (action === "read" || action === "read_list") {
            return (
              <Typography variant="caption" color="info.main">
                Viewed details
              </Typography>
            );
          }
          
          if (action === "update") {
            const changeDetail = describeAuditChanges(
              oldValue,
              newValue,
              params.row?.entityType,
              referenceMaps,
            );
            if (!changeDetail) {
              return (
                <Typography variant="caption" color="text.secondary">
                  No visible changes
                </Typography>
              );
            }

            return (
              <Typography variant="caption" color="warning.main" sx={{ fontWeight: 500 }}>
                {changeDetail}
              </Typography>
            );
          }
          
          return "-";
        },
      },
    ],
    [referenceMaps],
  );

  const onSearch = () => fetchLogs(1, pagination.limit);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Activity Logs
        </Typography>
        <Typography variant="body2" color="text.secondary">
          See what changes have been made to your institute data in a simple, easy-to-understand format.
        </Typography>
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: filtersExpanded ? 2 : 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: filtersExpanded ? 2 : 0 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Filters
            </Typography>
            <IconButton
              size="small"
              aria-label={filtersExpanded ? "Collapse filters" : "Expand filters"}
              onClick={() => setFiltersExpanded((v) => !v)}
            >
              {filtersExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          <Collapse in={filtersExpanded}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Entity Type"
                value={filters.entityType}
                onChange={(e) => setFilters((prev) => ({ ...prev, entityType: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Entity ID"
                value={filters.entityId}
                onChange={(e) => setFilters((prev) => ({ ...prev, entityId: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Action"
                value={filters.action}
                onChange={(e) => setFilters((prev) => ({ ...prev, action: e.target.value }))}
              >
                <MenuItem value="">All Actions</MenuItem>
                {ACTION_OPTIONS.map((action) => (
                  <MenuItem key={action} value={action}>
                    {getActionDescription(action)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="User name or ID"
                placeholder="Search by user name"
                value={filters.changedBy}
                onChange={(e) => setFilters((prev) => ({ ...prev, changedBy: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label="From Date"
                format={DATE_DISPLAY_FORMAT}
                value={filters.fromDate ? new Date(filters.fromDate) : null}
                onChange={(val) =>
                  setFilters((prev) => ({ ...prev, fromDate: toApiDate(val) }))
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label="To Date"
                format={DATE_DISPLAY_FORMAT}
                value={filters.toDate ? new Date(filters.toDate) : null}
                onChange={(val) =>
                  setFilters((prev) => ({ ...prev, toDate: toApiDate(val) }))
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="PII Reveals"
                value={filters.piiRevealOnly}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, piiRevealOnly: e.target.value }))
                }
                helperText="Audited unmask events"
              >
                <MenuItem value="">All Events</MenuItem>
                <MenuItem value="true">PII Reveals Only</MenuItem>
              </TextField>
            </Grid>
          </Grid>
          <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
            <Button variant="contained" onClick={onSearch}>
              Search
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                const cleared = {
                  entityType: "",
                  entityId: "",
                  action: "",
                  changedBy: "",
                  fromDate: "",
                  toDate: "",
                  piiRevealOnly: "",
                };
                setFilters(cleared);
                fetchLogs(1, pagination.limit, cleared);
              }}
            >
              Reset
            </Button>
          </Stack>
          </Collapse>
        </CardContent>
      </Card>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Card>
        <CardContent>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            autoHeight
            disableRowSelectionOnClick
              pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
            paginationMode="server"
            rowCount={pagination.totalItems || 0}
              paginationModel={{ page: Math.max(0, (pagination.page || 1) - 1), pageSize: pagination.limit || DEFAULT_PAGE_SIZE }}
            onPaginationModelChange={(model) => {
              fetchLogs(model.page + 1, model.pageSize);
            }}
            onRowClick={(params) => setSelectedLog(params.row)}
            sx={{ border: "none" }}
          />
        </CardContent>
      </Card>

      <Dialog
        open={Boolean(selectedLog)}
        onClose={() => setSelectedLog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Typography component="span" variant="h6" sx={{ pr: 2 }}>
              {selectedLog ? getAuditSummary(selectedLog) : "Activity Details"}
            </Typography>
            <IconButton aria-label="Close" onClick={() => setSelectedLog(null)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedLog ? (
            <Box sx={{ pt: 1 }}>
              <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
                <Chip size="small" label={`Type: ${getEntityDisplayName(selectedLog.entityType)}`} variant="outlined" />
                <Chip size="small" label={`ID: ${selectedLog.entityId}`} variant="outlined" />
                <Chip size="small" label={getActionDescription(selectedLog.action)} color={getActionChipColor(selectedLog.action)} />
                <Chip size="small" label={`When: ${formatAuditDate(selectedLog.changedAt)}`} variant="outlined" />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>By:</strong> {selectedLog.changedByName}
              </Typography>
              {(() => {
                const uaInfo = parseUserAgent(selectedLog.userAgent);
                if (!uaInfo && !selectedLog.ipAddress) return null;
                return (
                  <Stack spacing={0.5} sx={{ mb: 2 }}>
                    {uaInfo ? (
                      <Typography variant="body2" color="text.secondary">
                        <strong>Device:</strong> {uaInfo.summary}
                      </Typography>
                    ) : null}
                    {selectedLog.ipAddress ? (
                      <Typography variant="body2" color="text.secondary">
                        <strong>IP:</strong> {selectedLog.ipAddress}
                      </Typography>
                    ) : null}
                  </Stack>
                );
              })()}
              
              {(selectedLog.action === "update" || selectedLog.action === "create" || selectedLog.action === "delete") && (
                <>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                    {selectedLog.action === "update"
                      ? "What changed"
                      : selectedLog.action === "create"
                        ? "Details"
                        : "Removed record"}
                  </Typography>
                  {selectedLog.action === "create" ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      A new {getEntityDisplayName(selectedLog.entityType).toLowerCase()} was added with these details.
                    </Typography>
                  ) : null}
                  {selectedLog.action === "delete" ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      This {getEntityDisplayName(selectedLog.entityType).toLowerCase()} was deleted. These were its last known details.
                    </Typography>
                  ) : null}
                  {renderAuditDetailContent(selectedLog, formatValue)}
                </>
              )}
              
              {(selectedLog.action === "read" || selectedLog.action === "read_list") && (
                <>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    This was a view-only action. No data was modified.
                  </Typography>
                </>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setSelectedLog(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLogs;
